import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { newsList } from "@/mocks/news";
import usePageMeta from "@/hooks/usePageMeta";

export default function NewsDetailPage() {
  const { id = "" } = useParams();
  const item = newsList.find((news) => news.id === id);

  usePageMeta(
    item ? `${item.title} - 日韩黄色电影资讯` : "资讯不存在或已删除 - 日韩黄色电影",
    item
      ? item.summary
      : "该篇影视资讯不存在或已删除，请返回资讯列表浏览最新内容。",
    item ? `${item.category},影视资讯,日韩黄色电影,${item.title}` : "日韩黄色电影,影视资讯",
  );

  if (!item) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-6 py-24 md:py-32 bg-background-50">
          <div className="w-full max-w-[560px] mx-auto text-center">
            <span className="w-16 h-16 mx-auto rounded-full bg-background-100 border border-background-300/70 flex items-center justify-center text-2xl text-foreground-500">
              <i className="ri-article-line" />
            </span>
            <h1 className="mt-6 font-heading text-2xl text-foreground-950">资讯不存在或已删除</h1>
            <p className="mt-3 text-sm text-foreground-500 leading-relaxed">
              这篇资讯可能已被移除，或链接地址有误。
            </p>
            <Link
              to="/news"
              className="mt-7 inline-flex h-11 px-6 rounded-md bg-primary-500 text-foreground-950 text-sm items-center cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回资讯列表
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const related = newsList
    .filter((news) => news.id !== item.id)
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 3);

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6 pt-8 md:pt-12 pb-16 md:pb-24 bg-background-50">
        <div className="w-full max-w-[1200px] mx-auto">
          <nav className="flex flex-wrap items-center gap-1.5 text-[11px] md:text-xs text-foreground-500">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/news" className="hover:text-accent-500 transition-colors cursor-pointer">
              影视资讯
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-700 truncate max-w-[220px]">{item.title}</span>
          </nav>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8 lg:gap-12">
            <article className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 text-[11px]">
                <span className="px-2 py-1 rounded-md bg-accent-500/10 text-accent-500">
                  {item.category}
                </span>
                <span className="flex items-center gap-1 text-foreground-400">
                  <i className="ri-calendar-line" />
                  {item.date}
                </span>
              </div>

              <h1 className="mt-4 font-heading text-2xl md:text-[40px] leading-tight text-foreground-950">
                {item.title}
              </h1>

              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600 border-l-2 border-primary-500 pl-4">
                {item.summary}
              </p>

              <div className="mt-6 w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200 border border-background-300/60">
                <img
                  src={item.cover}
                  alt={`${item.title} 配图`}
                  title={`${item.title} - 日韩黄色电影资讯`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="mt-7 space-y-5">
                {item.paragraphs.map((paragraph, index) => (
                  <p
                    key={index}
                    className="text-sm md:text-[15px] leading-[1.95] text-foreground-600"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-6 border-t border-background-300/60">
                <Link
                  to="/news"
                  className="h-11 px-5 rounded-md border border-background-300/70 text-sm text-foreground-700 flex items-center justify-center gap-2 cursor-pointer hover:border-accent-500 hover:text-accent-500 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line" />
                  返回资讯列表
                </Link>
                <p className="text-[11px] text-foreground-400 leading-relaxed">
                  本文为演示虚构内容，仅用于展示资讯页面结构。
                </p>
              </div>
            </article>

            <aside className="min-w-0">
              <div className="lg:sticky lg:top-24">
                <h2 className="font-heading text-lg text-foreground-950 flex items-center gap-2">
                  <span className="w-1 h-4 rounded-full bg-accent-500" />
                  相关资讯
                </h2>
                <div className="mt-4 flex flex-col gap-3">
                  {related.map((news) => (
                    <Link
                      key={news.id}
                      to={`/news/detail/${news.id}`}
                      className="group rounded-lg border border-background-300/60 bg-background-100 p-3 flex gap-3 hover:border-primary-500/60 transition-colors cursor-pointer"
                    >
                      <div className="w-20 h-14 rounded-md overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={news.cover}
                          alt={`${news.title} 配图`}
                          title={news.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs leading-snug text-foreground-800 group-hover:text-accent-500 transition-colors line-clamp-2">
                          {news.title}
                        </p>
                        <p className="mt-1.5 text-[10px] text-foreground-400">{news.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}